'use client';

import { type ReactNode } from 'react';
import { ChevronLeft } from 'lucide-react';
import { Button } from '@zillow/constellation-10';
import { cx } from '@/styled-system/css';
import { LucideIcon } from '@/src/components/LucideIcon';
import { searchContentChromeGutter } from '@/src/layouts/searchContentGutters.styles';
import * as styles from './desktopStickyHeader.styles';

type DesktopStickyHeaderProps = {
  backHref: string;
  onBackClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  backLabel?: string;
  backAriaLabel?: string;
  forceUtilityLabels?: boolean;
  /**
   * Right-rail actions slot. HALO renders <ListingHeaderActions showCTAs />
   * here; HdpPage supplies it via the `desktopHeaderActions` slot on
   * ListingPage.
   */
  actions?: ReactNode;
};

export const DesktopStickyHeader = ({
  backHref,
  onBackClick,
  backLabel,
  backAriaLabel,
  forceUtilityLabels = false,
  actions,
}: DesktopStickyHeaderProps) => {
  const label = backLabel ?? 'Back to search';
  const ariaLabel = backAriaLabel ?? backLabel ?? 'Back to search results';

  const backControl = (
    <Button
      asChild
      tone="neutral"
      emphasis="bare"
      size="sm"
      icon={<LucideIcon icon={ChevronLeft} size="sm" />}
      className={styles.headerActionButton}
    >
      <a
        href={backHref}
        onClick={onBackClick}
        aria-label={ariaLabel}
        title={label}
        data-testid="link-back-to-search-results"
      >
        <span className={forceUtilityLabels ? undefined : styles.utilityActionLabel}>{label}</span>
      </a>
    </Button>
  );

  return (
    <header className={cx(styles.desktopHeader, searchContentChromeGutter)}>
      <div className={styles.desktopHeaderColumns}>
        <div className={styles.desktopHeaderLeft}>{backControl}</div>

        <div className={styles.desktopHeaderRight}>{actions}</div>
      </div>
    </header>
  );
};
